<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0
-->
<script>
import { createEventDispatcher, onDestroy } from 'svelte';

const dispatch = createEventDispatcher();

export let texts = {};

$: labels = texts || {};

let mediaRecorder = null;
let recordingStream = null;
let recordingChunks = [];
let isRecording = false;
let recordingDurationMs = 0;
let recordingTimer = null;
let recordingError = '';
let waveformCanvas;
let audioContext = null;
let audioAnalyser = null;
let waveformAnimationFrame = null;
let shouldDiscardRecording = false;

function setRecordingError(message) {
  recordingError = message;
  dispatch('recordingerror', { message });
}

function getSupportedRecordingMimeType() {
  // Browsers expose different MediaRecorder containers/codecs; prefer WebM/Opus when available.
  const candidates = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4'];
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return '';
  }
  return candidates.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || '';
}

function getRecordingExtension(mimeType) {
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('wav')) return 'wav';
  return 'webm';
}

function formatRecordingDuration(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function stopRecordingStream() {
  if (recordingStream) {
    recordingStream.getTracks().forEach((track) => track.stop());
    recordingStream = null;
  }
}

function clearRecordingTimer() {
  recordingDurationMs = 0;
  if (recordingTimer) {
    clearInterval(recordingTimer);
    recordingTimer = null;
  }
}

function clearWaveformCanvas() {
  if (!waveformCanvas) return;
  const context = waveformCanvas.getContext('2d');
  if (!context) return;
  context.clearRect(0, 0, waveformCanvas.width, waveformCanvas.height);
}

function drawWaveform() {
  if (!waveformCanvas || !audioAnalyser) return;

  const context = waveformCanvas.getContext('2d');
  if (!context) return;

  const width = waveformCanvas.clientWidth || 320;
  const height = waveformCanvas.clientHeight || 96;
  const pixelRatio = window.devicePixelRatio || 1;

  if (
    waveformCanvas.width !== Math.floor(width * pixelRatio) ||
    waveformCanvas.height !== Math.floor(height * pixelRatio)
  ) {
    waveformCanvas.width = Math.floor(width * pixelRatio);
    waveformCanvas.height = Math.floor(height * pixelRatio);
  }

  context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

  const data = new Uint8Array(audioAnalyser.fftSize);
  audioAnalyser.getByteTimeDomainData(data);

  context.clearRect(0, 0, width, height);
  context.fillStyle = getComputedStyle(waveformCanvas).backgroundColor || '#f2f2f2';
  context.fillRect(0, 0, width, height);
  context.lineWidth = 2;
  context.strokeStyle = isRecording ? '#555' : '#aaa';
  context.beginPath();

  const sliceWidth = width / data.length;
  let x = 0;

  for (let i = 0; i < data.length; i += 1) {
    const y = (data[i] / 128) * (height / 2);
    if (i === 0) {
      context.moveTo(x, y);
    } else {
      context.lineTo(x, y);
    }
    x += sliceWidth;
  }

  context.lineTo(width, height / 2);
  context.stroke();
  waveformAnimationFrame = requestAnimationFrame(drawWaveform);
}

function stopWaveform() {
  if (waveformAnimationFrame) {
    cancelAnimationFrame(waveformAnimationFrame);
    waveformAnimationFrame = null;
  }
  audioAnalyser = null;
  if (audioContext) {
    audioContext.close().catch(() => {});
    audioContext = null;
  }
  clearWaveformCanvas();
}

function startWaveform(stream) {
  const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextConstructor || !waveformCanvas) return;

  stopWaveform();
  audioContext = new AudioContextConstructor();
  const source = audioContext.createMediaStreamSource(stream);
  audioAnalyser = audioContext.createAnalyser();
  audioAnalyser.fftSize = 2048;
  source.connect(audioAnalyser);

  if (audioContext.state === 'suspended') {
    audioContext.resume().catch(() => {});
  }
  drawWaveform();
}

function createRecordingFile(blob, mimeType) {
  const extension = getRecordingExtension(mimeType);
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  return new File([blob], `recording-${timestamp}.${extension}`, {
    type: mimeType,
    lastModified: Date.now(),
  });
}

async function startRecording() {
  recordingError = '';

  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
    setRecordingError(labels.unsupportedBrowser);
    return;
  }

  try {
    recordingStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    startWaveform(recordingStream);

    const mimeType = getSupportedRecordingMimeType();
    const recorderOptions = mimeType ? { mimeType } : undefined;
    const recorder = new MediaRecorder(recordingStream, recorderOptions);

    recordingChunks = [];
    mediaRecorder = recorder;
    isRecording = true;
    recordingDurationMs = 0;
    shouldDiscardRecording = false;

    recorder.ondataavailable = (event) => {
      if (event.data?.size > 0) {
        recordingChunks.push(event.data);
      }
    };

    recorder.onerror = () => {
      setRecordingError(labels.recordingError);
      stopRecording({ discard: true });
    };

    recorder.onstop = () => {
      clearRecordingTimer();
      stopWaveform();
      stopRecordingStream();
      isRecording = false;

      const actualMimeType = recorder.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(recordingChunks, { type: actualMimeType });
      recordingChunks = [];
      mediaRecorder = null;

      if (shouldDiscardRecording) {
        shouldDiscardRecording = false;
        return;
      }

      if (blob.size === 0) {
        setRecordingError(labels.emptyRecording);
        return;
      }

      dispatch('recorded', {
        file: createRecordingFile(blob, actualMimeType),
      });
    };

    recorder.start();
    const startedAt = Date.now();
    recordingTimer = setInterval(() => {
      recordingDurationMs = Date.now() - startedAt;
    }, 250);
  } catch (error) {
    clearRecordingTimer();
    stopWaveform();
    stopRecordingStream();
    mediaRecorder = null;
    isRecording = false;
    setRecordingError(error?.name === 'NotAllowedError' ? labels.permissionDenied : labels.startError);
  }
}

function stopRecording({ discard = false } = {}) {
  shouldDiscardRecording = discard;
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop();
  }
}

function cancelRecording() {
  stopRecording({ discard: true });
}

function cleanupRecording() {
  clearRecordingTimer();
  stopWaveform();
  stopRecordingStream();
  recordingChunks = [];
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.onstop = null;
    mediaRecorder.stop();
  }
  mediaRecorder = null;
  isRecording = false;
}

onDestroy(cleanupRecording);
</script>

<div class="recorder-card">
  <div class="recorder-header">
    <strong>{labels.title}</strong>
    <span class:is-active={isRecording}>{isRecording ? labels.statusRecording : labels.statusReady}</span>
  </div>

  <canvas class="waveform" bind:this={waveformCanvas} aria-label={labels.waveformLabel}></canvas>

  <div class="recorder-actions">
    <button class="secondary recorder-button" on:click={isRecording ? stopRecording : startRecording} aria-pressed={isRecording}>
      {isRecording ? labels.finishButton : labels.startButton}
    </button>
    {#if isRecording}
      <button class="secondary recorder-button" on:click={cancelRecording}>
        {labels.cancelButton}
      </button>
    {/if}
    <span class="recording-duration">{formatRecordingDuration(recordingDurationMs)}</span>
  </div>

  <p class="recorder-hint">
    {labels.hint}
  </p>

  {#if recordingError}
    <p class="recorder-error">{recordingError}</p>
  {/if}
</div>

<style>
  .recorder-card {
    flex: 1;
    min-width: 280px;
    max-width: 520px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 18px;
    background: var(--bg-color);
  }
  .recorder-header {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    color: #333;
  }
  .recorder-header span {
    font-size: 0.9rem;
    color: #666;
  }
  .recorder-header span.is-active {
    color: #8e0e0e;
  }
  .waveform {
    width: 100%;
    height: 96px;
    display: block;
    background: var(--bg-color);
    border: 1px solid #d0d0d0;
  }
  .recorder-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
  .recorder-button {
    width: auto;
  }
  .recording-duration {
    min-width: 42px;
    color: #555;
    font-variant-numeric: tabular-nums;
  }
  .recorder-hint,
  .recorder-error {
    margin: 0;
    font-size: 0.9rem;
    color: #666;
  }
  .recorder-error {
    color: #b00020;
  }
</style>
